import * as React from "react";
import { Tag, Search, Plus, Edit2, Trash2, Check, X, ArrowLeft, Filter } from "lucide-react";
import { useLocation } from "wouter";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";
import { cn } from "@/lib/utils.js";

interface TagItem {
  name: string;
  count: number;
}

export default function TagsPage() {
  const [, navigate] = useLocation();
  const toast = useToast();
  const [tags, setTags] = React.useState<TagItem[]>([]);
  const [suggestions, setSuggestions] = React.useState<TagItem[]>([]);
  const [promotedTags, setPromotedTags] = React.useState<string[]>([]);
  const [search, setSearch] = React.useState("");
  const [newTag, setNewTag] = React.useState("");
  const [editingTag, setEditingTag] = React.useState<string | null>(null);
  const [editValue, setEditValue] = React.useState("");
  const [deleteTag, setDeleteTag] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  const fetchTags = React.useCallback(async () => {
    try {
      const [tagsData, settingsData] = await Promise.all([
        apiFetch<{ tags: TagItem[]; suggestions: TagItem[] }>("/api/tags"),
        apiFetch<{ promotedTags: string[] }>("/api/settings"),
      ]);
      setTags(tagsData.tags);
      setSuggestions(tagsData.suggestions);
      setPromotedTags(settingsData.promotedTags);
    } catch {
      toast.show("Failed to load tags", "error");
    }
    setLoading(false);
  }, []);

  React.useEffect(() => { fetchTags(); }, [fetchTags]);

  const handleAddTag = async () => {
    const name = newTag.trim().toLowerCase();
    if (!name) return;
    if (tags.some((t) => t.name === name)) {
      toast.show("Tag already exists", "error");
      return;
    }
    // To "create" a tag, we add it to promoted tags so it appears even with 0 items
    // Tags are created when items use them, but we can pre-register by adding to promoted
    const next = [...promotedTags, name];
    setPromotedTags(next);
    setTags([...tags, { name, count: 0 }]);
    setNewTag("");
    try {
      await apiFetch("/api/settings", { method: "PUT", body: JSON.stringify({ promotedTags: next }) });
      toast.show(`"${name}" added`);
    } catch {
      toast.show("Failed to save tag", "error");
      setPromotedTags(promotedTags);
      setTags(tags);
    }
  };

  const handleRename = async (oldName: string) => {
    const newName = editValue.trim().toLowerCase();
    if (!newName || newName === oldName) {
      setEditingTag(null);
      return;
    }
    try {
      await apiFetch(`/api/tags/${encodeURIComponent(oldName)}`, { method: "PUT", body: JSON.stringify({ newName }) });
      // Update promoted tags if the old name was promoted
      if (promotedTags.includes(oldName)) {
        const next = promotedTags.map((t) => t === oldName ? newName : t);
        setPromotedTags(next);
        await apiFetch("/api/settings", { method: "PUT", body: JSON.stringify({ promotedTags: next }) });
      }
      toast.show(`Renamed "${oldName}" to "${newName}"`);
      fetchTags();
    } catch (e: any) {
      toast.show(e.message ?? "Failed to rename tag", "error");
    }
    setEditingTag(null);
  };

  const handleDelete = async () => {
    if (!deleteTag) return;
    try {
      await apiFetch(`/api/tags/${encodeURIComponent(deleteTag)}`, { method: "DELETE" });
      // Remove from promoted if present
      if (promotedTags.includes(deleteTag)) {
        const next = promotedTags.filter((t) => t !== deleteTag);
        setPromotedTags(next);
        await apiFetch("/api/settings", { method: "PUT", body: JSON.stringify({ promotedTags: next }) });
      }
      toast.show(`"${deleteTag}" deleted`);
      fetchTags();
    } catch (e: any) {
      toast.show(e.message ?? "Failed to delete tag", "error");
    }
    setDeleteTag(null);
  };

  const togglePromoted = async (tagName: string) => {
    const isPromoted = promotedTags.includes(tagName);
    const next = isPromoted ? promotedTags.filter((t) => t !== tagName) : [...promotedTags, tagName];
    setPromotedTags(next);
    try {
      await apiFetch("/api/settings", { method: "PUT", body: JSON.stringify({ promotedTags: next }) });
      toast.show(isPromoted ? `"${tagName}" removed from filters` : `"${tagName}" promoted to filter`);
    } catch {
      setPromotedTags(promotedTags);
      toast.show("Failed to update", "error");
    }
  };

  const filtered = tags.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/settings")} aria-label="Back to settings" className="p-1.5 rounded-md text-faint hover:bg-amethyst-soft hover:text-amethyst-ink transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-semibold text-amethyst-ink flex items-center gap-2 font-headline tracking-[-0.01em]">
          <Tag className="w-6 h-6 text-primary" />Tags
        </h1>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-faint" />
        <input
          placeholder="Search tags..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
      </div>

      {/* Add new tag */}
      <div className="flex gap-2">
        <input
          placeholder="New tag name"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
          className="flex-1 px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
        <button onClick={handleAddTag} disabled={!newTag.trim()} aria-label="Add tag" className="px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Info box */}
      <div className="bg-amethyst-soft border border-clay-border rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Filter className="w-4 h-4 text-primary mt-0.5 shrink-0" />
          <p className="text-[0.8125rem] text-body">
            Toggle the filter icon to promote a tag as a quick-filter button on the Inventory page. Only promoted tags appear as filter pills.
          </p>
        </div>
      </div>

      {/* Tags list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-14 bg-paper-raised rounded-lg animate-pulse" />)}
        </div>
      ) : filtered.length === 0 && !search ? (
        <div className="text-center py-12 rounded-lg bg-sage-wash">
          <Tag className="w-10 h-10 text-sage mx-auto mb-3" />
          <p className="text-body text-sm">No tags yet. Add one above or they'll appear as you tag items.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((tag) => {
            const isEditing = editingTag === tag.name;
            const isPromoted = promotedTags.includes(tag.name);

            return (
              <div key={tag.name} className="flex items-center gap-3 p-3 rounded-lg bg-card border border-clay-border hover:border-primary/40 transition-colors">
                {isEditing ? (
                  <>
                    <input
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleRename(tag.name); if (e.key === "Escape") setEditingTag(null); }}
                      autoFocus
                      className="flex-1 px-3 py-1.5 rounded-sm border border-primary text-sm text-body focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    <button onClick={() => handleRename(tag.name)} aria-label="Save tag name" className="p-1.5 rounded-md hover:bg-success-wash text-success transition-colors">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditingTag(null)} aria-label="Cancel rename" className="p-1.5 rounded-md hover:bg-amethyst-soft text-faint transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-body truncate">{tag.name}</span>
                        <span className="text-[0.8125rem] text-faint tabular-nums">{tag.count} {tag.count === 1 ? "item" : "items"}</span>
                      </div>
                    </div>
                    {/* Promote as filter toggle */}
                    <button
                      onClick={() => togglePromoted(tag.name)}
                      title={isPromoted ? "Remove from inventory filters" : "Promote to inventory filter"}
                      aria-label={isPromoted ? "Remove from inventory filters" : "Promote to inventory filter"}
                      aria-pressed={isPromoted}
                      className={cn(
                        "p-1.5 rounded-md transition-colors",
                        isPromoted ? "bg-amethyst-soft text-primary" : "text-faint hover:text-primary hover:bg-amethyst-soft/60"
                      )}
                    >
                      <Filter className="w-4 h-4" />
                    </button>
                    {/* Edit */}
                    <button onClick={() => { setEditingTag(tag.name); setEditValue(tag.name); }} aria-label={`Rename ${tag.name}`} className="p-1.5 rounded-md hover:bg-amethyst-soft text-faint hover:text-primary transition-colors">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {/* Delete */}
                    <button onClick={() => setDeleteTag(tag.name)} aria-label={`Delete ${tag.name}`} className="p-1.5 rounded-md hover:bg-error-wash text-faint hover:text-error transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            );
          })}
          {search && filtered.length === 0 && (
            <p className="text-center text-sm text-faint py-4">No tags matching "{search}"</p>
          )}
        </div>
      )}

      {/* Suggestions (for new households) */}
      {suggestions.length > 0 && !search && (
        <section className="mt-6">
          <h3 className="text-[0.8125rem] font-medium text-faint uppercase tracking-wide mb-3">Suggested Tags</h3>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s.name}
                onClick={() => {
                  setTags([...tags, { name: s.name, count: 0 }]);
                  setSuggestions(suggestions.filter((x) => x.name !== s.name));
                  const next = [...promotedTags, s.name];
                  setPromotedTags(next);
                  apiFetch("/api/settings", { method: "PUT", body: JSON.stringify({ promotedTags: next }) }).catch(() => {});
                  toast.show(`"${s.name}" added`);
                }}
                className="px-3 py-1.5 rounded-pill bg-clay-muted text-sm text-body hover:bg-amethyst-soft hover:text-amethyst-ink border border-clay-border flex items-center gap-1 transition-colors"
              >
                <Plus className="w-3 h-3" />{s.name}
              </button>
            ))}
          </div>
        </section>
      )}

      <ConfirmDialog
        open={!!deleteTag}
        title="Delete Tag"
        message={`Delete "${deleteTag}" from all items? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
        onCancel={() => setDeleteTag(null)}
      />
    </div>
  );
}
